import { createContext, useContext, useCallback } from "react";
import useApiService from "./ApiService";
import CONFIG from "./CONFIG";

const AgentApiContext = createContext(null);

export function AgentApiProvider({ children }) {

    const { get, post, put, del } = useApiService(CONFIG.SERVER_ASSISTANT);

    const organizationApi = useApiService(CONFIG.SERVER_ORGANIZATION);

    const documentApi = useApiService(CONFIG.SERVER_DOCUMENT);

    const getAgents = useCallback(async (skip, limit) => {
        if (!skip) {
            skip = 0;
        }
        if (!limit) {
            limit = 10;
        }
        try {
            const response = await get(`/v1/assistants?skip=${skip}&limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agents:", error);
            throw error;
        }
    }, [get]);

    const getAgentById = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent by ID:", error);
            throw error;
        }
    }, [get]);

    const getAgentNotifications = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}/notifications`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent notifications:", error);
            throw error;
        }
    }, [get]);

    const getAgentCatalog = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}/catalog`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent catalog:", error);
            throw error;
        }
    }, [get]);

    const getAgentFrames = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}/assistant_frames`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent frames:", error);
            throw error;
        }
    }, [get]);

    const createAgent = useCallback(async (agent) => {
        try {
            const response = await put(`/v1/assistants`, agent);
            return response.data;
        } catch (error) {
            console.error("Error creating agent:", error);
            throw error;
        }
    }, [put]);

    const updateAgentById = useCallback(async (agentId, agent) => {
        try {
            const response = await put(`/v1/assistants/${agentId}`, agent);
            return response.data;
        } catch (error) {
            console.error("Error updating agent:", error);
            throw error;
        }
    }, [put]);

    const deleteAgent = useCallback(async (agentId) => {
        try {
            const response = await del(`/v1/assistants/${agentId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting agent:", error);
            throw error;
        }
    }, [del]);

    const getNotifications = useCallback(async () => {
        try {
            const response = await get(`/v1/notifications`);
            return response.data.items ?? [];
        } catch (error) {
            console.error("Error fetching notifications:", error);
            throw error;
        }
    }, [get]);

    const getPrompts = useCallback(async () => {
        try {
            const response = await get(`/v1/prompts`);
            return response.data.items;
        } catch (error) {
            console.error("Error fetching prompts:", error);
            throw error;
        }
    }, [get]);

    const getFrames = useCallback(async () => {
        try {
            const response = await get(`/v1/assistant_frames?limit=500`);
            return response.data.items;
        } catch (error) {
            console.error("Error fetching frames:", error);
            throw error;
        }
    }, [get]);

    const getMarkers = useCallback(async () => {
        try {
            const response = await get(`/v1/markers?limit=500`);
            return response.data.items;
        } catch (error) {
            console.error("Error fetching markers:", error);
            throw error;
        }
    }, [get]);

    const getAgentMarkers = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}/assistant_markers`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent markers:", error);
            throw error;
        }
    }, [get]);

    const getCatalogs = useCallback(async () => {
        try {
            const response = await documentApi.get(`/v1/e_commerce/catalogs`);
            return response.data.items ?? [];
        } catch (error) {
            console.error("Error fetching product catalogs:", error);
            throw error;
        }
    }, [documentApi]);

    const getDictionaries = useCallback(async () => {
        try {
            const response = await get(`/v1/taxonomy_dictionary`);
            return response.data.items ?? [];
        } catch (error) {
            console.error("Error fetching dictionaries:", error);
            throw error;
        }
    }, [get]);

    const executeAgent = useCallback(async (agentId, messages) => {
        try {
            const payload = messages.map(item => {
                if (item.from === 'user') {
                    return { "human": item.rawText };
                }
                else if (item.from === 'assistant') {
                    return { "ai": item.rawText };
                }
            })
            console.log("Payload to Assistant:", payload);
            console.log("Data to Assistant:", messages);
            const response = await post(`/v1/assistants/${agentId}/streams`, { thread: payload });
            const responseThread = response.data.thread.map(item => {
                if (item.ai){
                    return {
                        from: 'assistant',
                        rawText: item.ai,
                        time: new Date().toTimeString().slice(0, 8),
                        metadata: item.metadata
                    }
                } else if (item.human) {
                    return {
                        from: 'user',
                        rawText: item.human,
                        time: new Date().toTimeString().slice(0, 8)
                    }
                }
            })
            return responseThread;
        } catch (error) {
            console.error("Error executing agent:", error);
            throw error;
        }
    }, [post]);


    const getAgentInstructions = useCallback(async (agentId, skip, limit) => {
        if (!skip) {
            skip = 0;
        }
        if (!limit) {
            limit = 10;
        }
        try {
            const response = await get(`/v1/assistants/${agentId}/instructions?skip=${skip}&limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent instructions", error);
            throw error;
        }
    }, [get]);

    const getAgentAuditOperations = useCallback(async (agentId, skip, limit, start_date, end_date, id = null) => {
        if (!skip) {
            skip = 0;
        }
        if (!limit) {
            limit = 10;
        }
        try {
            let url = `/v1/assistants/${agentId}/audit_operations?skip=${skip}&limit=${limit}&start_date=${start_date}&end_date=${end_date}`;
            if (id) {
                url += `&id=${id}`;
            }
            const response = await get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent audit operation:", error);
            throw error;
        }
    }, [get]);

    const getAgentAuditOperationById = useCallback(async (agentId, auditId) => {
    try {
        const response = await get(
          `/v1/assistants/${agentId}/audit_operations/${auditId}`
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching agent audit operation:", error);
        throw error;
    }
    }, [get]);

    const createAgentInstruction = useCallback(async (agentId, instruction) => {
        try {
            const response = await post(`/v1/assistants/${agentId}/instructions`, instruction);
            return response.data;
        } catch (error) {
            console.error("Error creating agent insturction:", error);
            throw error;
        }
    }, [post]);

    const updateAgentInstruction = useCallback(async (agentId, instructionID, instruction) => {
        try {
            const response = await put(
                `/v1/assistants/${agentId}/instructions/${instructionID}`,
                instruction
            );
            return response.data;
        } catch (error) {
            console.error("Error updating agent instruction:", error);
            throw error;
        }
    }, [put]);

    const deleteAgentInstruction = useCallback(async (agentId, instructionID) => {
        try {
            const response = await del(`/v1/assistants/${agentId}/instructions/${instructionID}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting agent instruction:", error);
            throw error;
        }
    }, [del]);

    const getRelevantInstruction = useCallback(async (agentId, search, limit) => {
        if (!limit) {
            limit = 10;
        }
        if (!search) {
            search = '';
        }
        try {
            const response = await get(`/v1/assistants/${agentId}/instructions/find_relevant_instruction?search=${search}&limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching agent instructions", error);
            throw error;
        }
    }, [get]);

    const getAllSwarm = useCallback(async (skip, limit) => {
        if (!skip) {
            skip = 0;
        }
        if (!limit) {
            limit = 10;
        }
        try {
            const response = await get(`/v1/assistant_swarms?skip=${skip}&limit=${limit}`);
            return response.data.items;
        } catch (error) {
            console.error("Error fetching swarm agents:", error);
            throw error;
        }
    }, [get]);

    const getAgentSwarm = useCallback(async (agentId) => {
        try {
            const response = await get(`/v1/assistants/${agentId}/swarms`);
            return response.data;
        } catch (error) {
            console.error("Error fetching swarm agents:", error);
            throw error;
        }
    }, [get]);

    const getDocumentCollections = useCallback(async () => {
        try {
            const response = await documentApi.get(`/v1/document_collection`);
            return response.data.items ?? [];
        } catch (error) {
            console.error("Error fetching document collections:", error);
            throw error;
        }
    }, [documentApi]);

    const getConnections = useCallback(async () => {
        try {
            const response = await organizationApi.get(`/v1/connections`);
            return response.data.items ?? [];
        } catch (error) {
            console.error("Error fetching swarm agents:", error);
            throw error;
        }
    }, [get]);

    return (
        <AgentApiContext.Provider value={{ 
            getAgents,
            getAgentById,
            getAgentNotifications,
            getAgentCatalog,
            getAgentFrames,
            getAgentMarkers,
            createAgent,
            updateAgentById,
            deleteAgent,
            getNotifications,
            getPrompts,
            getFrames,
            getMarkers,
            getCatalogs,
            executeAgent,
            getAgentInstructions,
            createAgentInstruction,
            updateAgentInstruction,
            deleteAgentInstruction,
            getDictionaries,
            getRelevantInstruction,
            getAgentAuditOperations,
            getAllSwarm,
            getAgentSwarm,
            getDocumentCollections,
            getConnections,
            getAgentAuditOperationById
        }}>
            {children}
        </AgentApiContext.Provider>
    );
}

export function useAgentApi() {
    const context = useContext(AgentApiContext);
    if (!context) {
        throw new Error("useAgentApi must be used within a AgentApiContext");
    }
    return context;
}